import { TaskActivityEventType, TaskActivityMonitor } from 'Core/TaskActivityMonitor'
import { Timer, TimerType } from 'Core/Timer'
import { ITask } from './TaskAdapter'
import { CommonTask } from 'Tasks/CommonTask/CommonTask'
import { ContainerType } from 'Types/Tasks.types'
import { InactivityModalController } from './ModalCore/Modals/InactivityModalController'

type TaskActivityControllerConfig =
    | {
          is_enabled: boolean
          duration_in_minutes: number
      }
    | undefined

const RESPONSE_TIMEOUT_SECONDS = 15
const DEFAULT_DURATION_IN_MINUTES = 5
export class TaskActivityController {
    private readonly timer: Timer
    private readonly activityMonitor: TaskActivityMonitor
    private readonly getTask: () => ITask | undefined
    private subscriptionIds: string[] = []

    constructor(
        config: TaskActivityControllerConfig,
        timer: Timer,
        getTask: () => ITask | undefined
    ) {
        this.timer = timer
        this.getTask = getTask
        this.activityMonitor = new TaskActivityMonitor({
            isEnabled: config?.is_enabled ?? false,
            minimumDurationMinutes: config?.duration_in_minutes ?? DEFAULT_DURATION_IN_MINUTES,
            responseTimeoutSeconds: RESPONSE_TIMEOUT_SECONDS,
        })
        this.setupSubscriptions()
    }

    start() {
        if (this.activityMonitor.getConfig().isEnabled) {
            this.activityMonitor.startMonitoring()
        }
    }

    stop() {
        this.activityMonitor.stopMonitoring()
        this.cleanupSubscriptions()
    }

    private setupSubscriptions() {
        this.subscriptionIds.push(
            this.activityMonitor.on(
                TaskActivityEventType.InactivityThreshold,
                this.showInactivityModal.bind(this)
            )
        )
        this.subscriptionIds.push(
            this.activityMonitor.on(
                TaskActivityEventType.UserInactive,
                this.pauseTaskDueToInactivity.bind(this)
            )
        )
    }

    private cleanupSubscriptions() {
        this.subscriptionIds.forEach(id => this.activityMonitor.off(id))
        this.subscriptionIds = []
    }

    private showInactivityModal() {
        const task = this.getTask() as unknown as CommonTask<ContainerType, unknown> | undefined
        if (!task) {
            return
        }
        // Pause monitoring while modal is shown to prevent multiple triggers
        this.activityMonitor.pauseMonitoring()
        task.getModalManager()?.setModalMetaData(InactivityModalController.Id, {
            onConfirm: this.handleUserConfirmsActive.bind(this),
            isResumeModal: false,
        })
        task.getModalManager()?.showModal(InactivityModalController.Id)
    }

    private handleUserConfirmsActive() {
        this.activityMonitor.resumeMonitoring()
    }

    private pauseTaskDueToInactivity() {
        this.timer.pause()
        ;(this.getTask() as unknown as CommonTask<ContainerType, unknown> | undefined)?.saveTask(
            false
        )
        this.activityMonitor.pauseMonitoring()
        this.showResumeModal()
    }

    private showResumeModal() {
        const task = this.getTask() as unknown as CommonTask<ContainerType, unknown> | undefined
        if (!task) {
            return
        }
        task.getModalManager()?.setModalMetaData(InactivityModalController.Id, {
            onConfirm: () => this.handleResumeTask(),
            isResumeModal: true,
        })
        task.getModalManager()?.showModal(InactivityModalController.Id)
    }

    private handleResumeTask() {
        this.timer.start({
            type: TimerType.CountUp,
            elapsedTimeInSec: this.timer.getElapsedTime() / 1000,
        })
        this.activityMonitor.resumeMonitoring()
    }
}
